import { BaseEntityType } from './entities';
import { AnyWindowEntity } from './windows';

// Single source of truth for settings
export interface SpaceSettings {
  isContentsVisible: boolean;
  isChatVisible: boolean;
  bgColor?: string;
  viewMode?: 'spatial' | 'focused' | 'gui2' | 'home' | 'canvas';
  background?: SpaceBackground;
  sidebar?: {
    isVisible: boolean;
    width: number;
    mode?: 'normal' | 'overlay';
  };
  spaceChat?: {
    currentThreadIds: Record<string, string>; // Legacy - kept for backward compat
    isHistoryOpen: boolean;
    mode: string;
    model: string;
    isContextVisible: boolean;
    widths: { focused: number; spatial: number; };
    draftMessages?: Record<string, string>; // threadId -> draft text
    toneSettings?: Record<string, number | string | undefined>; // ✅ NEW: User tone preferences
    // ✅ CHAT TABS: Tab state
    tabs?: Array<{
      id: string;
      threadId: string;
      mode: string;
      title?: string;
      isPinned?: boolean;
      createdAt: string;
      lastActiveAt: string;
    }>;
    activeTabId?: string | null;
  };
  entitySidebar?: {
    expandedFolderIds: string[];
    currentFolderId: string | null;
    folderPath: Array<{ id: string; name: string }>;
  };
  contextState?: any; // Use any to avoid circular dependency - contextStore manages the actual types
}

// Background types
export interface SpaceBackground {
  type: 'image' | 'color' | 'pattern' | 'preset';
  src?: string; // for images
  color?: string; // for color backgrounds
  value?: string; // hex color, pattern class, image data URL, or preset background id
  patternColor?: string; // for patterns only
  patternBgColor?: string; // for patterns only
}

// Backend skeleton format
interface SpaceSkeleton {
  "@type": "Space";
  name: string;
  windows: Record<string, AnyWindowEntity>;
  settings: SpaceSettings;
  contextEntities?: Record<string, { llmId: string; windowId?: string; isManuallySet: boolean; addedAt: number }>; // ✅ NEW: Persistent context state
  contextExcerpts?: any[]; // ✅ NEW: Persistent entity excerpts (pasted/selections)
}

// Backend entity format
export interface SpaceEntityBackend extends Omit<BaseEntityType<SpaceSkeleton>, 'skeleton'> {
  entityType: "Space";
  skeleton: SpaceSkeleton;
  user_id: string;
  acl?: ACLEntry[];
  activeWindowId?: string | null;
  created_at: string; // ISO string from backend
  updated_at: string; // ISO string from backend
  org_id?: string; // ✅ ORGS V1: Organization ID for dataspace scoping
}

// Old interface for backwards compatibility
export interface SpaceEntity extends SpaceEntityBackend {}

// Frontend UI format
export interface UISpace {
  id: string;
  name: string;
  windows: Record<string, AnyWindowEntity>;
  windowOrder: string[]; // NEW: Prevents jitter
  contextEntities?: Record<string, { llmId: string; windowId?: string; isManuallySet: boolean; addedAt: number }>; // ✅ NEW: Persistent context state
  contextExcerpts?: any[]; // ✅ NEW: Persistent entity excerpts (pasted/selections)
  activeWindowId?: string | null;
  settings: SpaceSettings;
  createdAt: string; // ISO string - convert to Date only when needed
  updatedAt: string; // ISO string
  acl?: ACLEntry[];
  org_id?: string; // ✅ ORGS V1: Organization ID for dataspace scoping
}

// Typed errors for better error handling
export class SpaceAccessError extends Error {
  constructor(message: string, public spaceId: string) {
    super(message);
    this.name = 'SpaceAccessError';
  }
}

export class PaymentRequiredError extends Error {
  constructor(message: string = 'Payment required') {
    super(message);
    this.name = 'PaymentRequiredError';
  }
}

export class SpaceNotFoundError extends Error {
  constructor(public spaceId: string) {
    super(`Space not found: ${spaceId}`);
    this.name = 'SpaceNotFoundError';
  }
}

export class DocumentAccessError extends Error {
  constructor(message: string, public documentId: string, public documentName?: string) {
    super(message);
    this.name = 'DocumentAccessError';
  }
}

export class SpaceCreationError extends Error {
  constructor(message: string, public cause?: any) {
    super(message);
    this.name = 'SpaceCreationError';
  }
}

// Transform utilities
// NOTE: Backend returns BOTH nested (skeleton.settings) and flattened (settings) fields
// This is a mess but we need to handle both for compatibility
export const transformBackendSpaceToUI = (backend: any): UISpace => {
  // Backend already flattens these fields - use them directly
  const windows = backend.windows || backend.skeleton?.windows || {};
  const settings = backend.settings || backend.skeleton?.settings || {};
  const contextEntities = backend.contextEntities || backend.skeleton?.contextEntities || {};
  const contextExcerpts = backend.contextExcerpts || backend.skeleton?.contextExcerpts || [];
  
  return {
    id: backend._id || backend.id,
    name: backend.name,
    windows,
    windowOrder: Object.keys(windows), // Maintain order
    contextEntities,
    contextExcerpts,
    activeWindowId: backend.activeWindowId || null,
    settings,
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    acl: backend.acl || [],
    org_id: backend.org_id, // ✅ ORGS V1: Preserve org_id (now included in backend response)
  };
};

export const transformUISpaceToBackend = (ui: UISpace): Partial<SpaceEntityBackend> => {
  return {
    name: ui.name,
    skeleton: {
      "@type": "Space",
      name: ui.name,
      windows: ui.windows,
      settings: ui.settings,
      contextEntities: ui.contextEntities, // ✅ NEW: Map contextEntities to skeleton
      contextExcerpts: ui.contextExcerpts || [],
    },
    activeWindowId: ui.activeWindowId,
  };
};

// CRDT Types
export interface WindowsUpdate {
  type: 'windows';
  data: AnyWindowEntity[];
}

export interface SettingsUpdate {
  type: 'settings';
  data: SpaceSettings;
}

export type SpaceCRDTUpdate = (WindowsUpdate | SettingsUpdate) & {
  timestamp: number;
};

// ACL Types
export interface ACLRole {
  OWNER: 'owner';
  EDITOR: 'editor';
  VIEWER: 'viewer';
}

export interface ACLEntry {
  subject: string; // User Auth0 ID
  role: 'owner' | 'editor' | 'viewer';
  granted_at?: string;
  granted_by?: string;
}

export interface ACLEntryWithUser extends ACLEntry {
  auth0Id: string;
  status?: 'active' | 'pending';
  user: {
    email?: string;
    name?: string;
    picture?: string;
  };
}

// Space initialization states
export type SpaceInitializationStatus = 
  | 'idle'
  | 'checking-auth'
  | 'checking-payment'
  | 'loading-recent'
  | 'creating-new'
  | 'loading-space'
  | 'initialized'
  | 'error';

export interface SpaceInitializationState {
  status: SpaceInitializationStatus;
  progress?: string;
  error?: Error;
} 